import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { QrCode, Plus, Download, Upload, Image as ImageIcon, GripVertical, Trash2, ImagePlus, Edit, Printer, X, Monitor } from "lucide-react";
import PrefixImageEditor from "../../components/PrefixImageEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("circle");
  const [autoPrint, setAutoPrint] = useState(false);
  const [fromAddress, setFromAddress] = useState("From:\nPOTRAIT CHOCOLATE\n55-230 Hoea Rd\nHawi, HI 96719");
  const [toAddress, setToAddress] = useState("To:\nName\nAddress\nCity, State ZIP");
  const [ingredients, setIngredients] = useState("Potato starch, water, sunflower oil, edible colors. (water, propylene glycol, glycerin, and various FDA-certified food coloring dyes)");
  const [nutritionFacts, setNutritionFacts] = useState("Calories: 0g\nCarbohydrates: 7g\nFat: 0g\nProtein: 0g\nSugars: 0g");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateImages, setTemplateImages] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [showImageView, setShowImageView] = useState(false);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [ownerImageFile, setOwnerImageFile] = useState(null);
  const [uploadingOwnerImage, setUploadingOwnerImage] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [showPrinterSetup, setShowPrinterSetup] = useState(false);
  const [selectedTemplateToPrint, setSelectedTemplateToPrint] = useState(null);
  const [prefixImageFile, setPrefixImageFile] = useState(null);
  const [showAccessControl, setShowAccessControl] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [selectedTemplateForAccess, setSelectedTemplateForAccess] = useState(null);
  const [showPrefixEditor, setShowPrefixEditor] = useState(false);
  const [selectedTemplateForPrefix, setSelectedTemplateForPrefix] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchPrinters();
  }, []);

  const fetchPrinters = async () => {
    try {
      const response = await axios.get(`${API}/printers`);
      setAvailablePrinters(response.data.printers || []);
    } catch (error) {
      console.error("Failed to fetch printers");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      alert("Failed to load templates");
    }
  };

  const createTemplate = async () => {
    if (!newTemplateName.trim()) {
      alert("Please enter a template name");
      return;
    }

    try {
      const templateData = { 
        name: newTemplateName,
        template_type: templateType,
        auto_print: autoPrint
      };
      
      // Add address fields for postcard templates
      if (templateType === "postcard" || templateType === "text-postcard") {
        templateData.from_address = fromAddress;
        templateData.to_address = toAddress;
      }
      
      // Add extra fields for text-postcard
      if (templateType === "text-postcard") {
        templateData.ingredients = ingredients;
        templateData.nutrition_facts = nutritionFacts;
      }
      
      await axios.post(`${API}/templates`, templateData);
      alert("Template created successfully");
      setNewTemplateName("");
      setTemplateType("circle");
      setAutoPrint(false);
      setFromAddress("From:\nPOTRAIT CHOCOLATE\n55-230 Hoea Rd\nHawi, HI 96719");
      setToAddress("To:\nName\nAddress\nCity, State ZIP");
      setIngredients("Potato starch, water, sunflower oil, edible colors. (water, propylene glycol, glycerin, and various FDA-certified food coloring dyes)");
      setNutritionFacts("Calories: 0g\nCarbohydrates: 7g\nFat: 0g\nProtein: 0g\nSugars: 0g");
      fetchTemplates();
    } catch (error) {
      console.error("Template creation error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
      alert(`Failed to create template: ${errorMessage}`);
    }
  };

  const uploadBackground = async (templateId) => {
    if (!backgroundFile) {
      alert("Please select a background image");
      return;
    }

    const formData = new FormData();
    formData.append("file", backgroundFile);

    try {
      await axios.post(`${API}/templates/${templateId}/background`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Background uploaded successfully");
      setBackgroundFile(null);
      fetchTemplates();
    } catch (error) {
      alert("Failed to upload background");
    }
  };

  const viewQRCode = (templateId) => {
    setQrCodeUrl(`${API}/templates/${templateId}/qr`);
    setShowQR(true);
  };

  const viewImages = async (template) => {
    try {
      const response = await axios.get(`${API}/templates/${template.id}/images`);
      setTemplateImages(response.data);
      setSelectedTemplate(template);
      setShowImageView(true);
    } catch (error) {
      alert("Failed to load images");
    }
  };

  const downloadPDF = async (templateId, templateName) => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/pdf`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${templateName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert("PDF downloaded successfully");
    } catch (error) {
      alert("Failed to download PDF");
    }
  };

  const moveImage = async (imageId, newIndex) => {
    try {
      await axios.put(`${API}/templates/${selectedTemplate.id}/move-image`, {
        image_id: imageId,
        new_circle_index: newIndex,
      });
      alert("Image position updated");
      viewImages(selectedTemplate);
    } catch (error) {
      alert("Failed to move image");
    }
  };

  const uploadOwnerImage = async (templateId) => {
    if (!ownerImageFile) {
      alert("Please select an image");
      return;
    }

    setUploadingOwnerImage(true);
    const formData = new FormData();
    formData.append("file", ownerImageFile);

    try {
      await axios.post(`${API}/templates/${templateId}/upload-owner`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Image uploaded successfully");
      setOwnerImageFile(null);
      fetchTemplates();
      if (selectedTemplate && selectedTemplate.id === templateId) {
        viewImages(selectedTemplate);
      }
    } catch (error) {
      if (error.response?.status === 507) {
        alert("Out of memory. Please try with a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert("Failed to upload image");
      }
    } finally {
      setUploadingOwnerImage(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    try {
      await axios.delete(`${API}/images/${imageId}`);
      alert("Image deleted successfully");
      fetchTemplates();
      if (selectedTemplate) {
        viewImages(selectedTemplate);
      }
    } catch (error) {
      alert("Failed to delete image");
    }
  };

  const printTemplate = async (templateId, templateName) => {
    const template = templates.find(t => t.id === templateId);
    
    // Check if printer is configured
    if (!template?.printer_name && availablePrinters.length === 0) {
      // No printers available - show setup dialog
      setSelectedTemplateToPrint(template);
      setShowPrinterSetup(true);
      return;
    }
    
    if (!template?.printer_name && availablePrinters.length > 0) {
      // Printers available but not configured for this template
      setSelectedTemplateToPrint(template);
      setShowPrinterSetup(true);
      return;
    }

    try {
      // Try to print
      const response = await axios.post(`${API}/templates/${templateId}/print`);
      alert("Print job sent to printer");
    } catch (error) {
      // If printing fails, download PDF instead
      if (error.response?.status === 500) {
        alert("No printer configured. Downloading PDF instead...");
        downloadPDF(templateId, templateName);
      } else {
        alert(error.response?.data?.detail || "Print failed");
      }
    }
  };

  const configurePrinter = async (printerName) => {
    if (!selectedTemplateToPrint) return;

    try {
      await axios.put(`${API}/templates/${selectedTemplateToPrint.id}/printer?printer_name=${printerName}&auto_print=false`);
      alert(`Printer "${printerName}" configured`);
      setShowPrinterSetup(false);
      fetchTemplates();
      
      // Now try to print
      printTemplate(selectedTemplateToPrint.id, selectedTemplateToPrint.name);
    } catch (error) {
      alert("Failed to configure printer");
    }
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"? This will remove all associated images and data.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/templates/${templateId}`);
      alert("Template deleted successfully");
      fetchTemplates();
    } catch (error) {
      alert("Failed to delete template");
    }
  };

  const uploadPrefixImage = async (templateId) => {
    if (!prefixImageFile) {
      alert("Please select a prefix/branding image");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", prefixImageFile);
      await axios.post(`${API}/templates/${templateId}/prefix-image`, formData);
      alert("Prefix image uploaded successfully");
      setPrefixImageFile(null);
      fetchTemplates();
    } catch (error) {
      alert("Failed to upload prefix image");
    }
  };

  const addEmailAccess = async () => {
    if (!accessEmail.trim() || !selectedTemplateForAccess) {
      alert("Please enter an email");
      return;
    }

    try {
      await axios.put(`${API}/templates/${selectedTemplateForAccess.id}/access`, {
        action: "add",
        email: accessEmail.trim()
      });
      alert(`Access granted to ${accessEmail}`);
      setAccessEmail("");
      fetchTemplates();
      // Update selected template for access
      const updated = templates.find(t => t.id === selectedTemplateForAccess.id);
      if (updated) {
        setSelectedTemplateForAccess({...updated, allowed_emails: [...(updated.allowed_emails || []), accessEmail.trim()]});
      }
    } catch (error) {
      alert("Failed to add email access");
    }
  };

  const removeEmailAccess = async (email) => {
    if (!selectedTemplateForAccess) return;

    try {
      await axios.put(`${API}/templates/${selectedTemplateForAccess.id}/access`, {
        action: "remove",
        email: email
      });
      alert(`Access removed for ${email}`);
      fetchTemplates();
      // Update selected template
      setSelectedTemplateForAccess({
        ...selectedTemplateForAccess,
        allowed_emails: (selectedTemplateForAccess.allowed_emails || []).filter(e => e !== email)
      });
    } catch (error) {
      alert("Failed to remove email access");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="mb-12">
          <h1 className="text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            QR Circle Templates
          </h1>
          <p className="text-lg text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>Create, manage, and print your event photo templates</p>
        </div>

        <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90" data-testid="create-template-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <Plus className="w-6 h-6" />
              Create New Template
            </CardTitle>
            <CardDescription>Start a new template for your event or party</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Input
                  data-testid="template-name-input"
                  placeholder="Enter template name (e.g., Birthday Party 2025)"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && createTemplate()}
                  className="flex-1"
                />
                <Button 
                  data-testid="create-template-btn"
                  onClick={createTemplate}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  Create
                </Button>
              </div>
              
              <div className="flex gap-3">
                <button
                  data-testid="circle-template-type-btn"
                  onClick={() => setTemplateType("circle")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "circle" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">⭕</div>
                    <div className="font-semibold">Circle (2")</div>
                    <div className="text-xs text-gray-500 mt-1">1.97" circles • ~15 per page</div>
                  </div>
                </button>
                
                <button
                  data-testid="circle-small-template-type-btn"
                  onClick={() => setTemplateType("circle-small")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "circle-small" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">🔴</div>
                    <div className="font-semibold">Circle (1.5")</div>
                    <div className="text-xs text-gray-500 mt-1">1.5" circles • ~30 per page</div>
                  </div>
                </button>
                
                <button
                  data-testid="rectangle-template-type-btn"
                  onClick={() => setTemplateType("rectangle")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "rectangle" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">▭</div>
                    <div className="font-semibold">Rectangle Template</div>
                    <div className="text-xs text-gray-500 mt-1">4"×6" boxes • 4 per page</div>
                  </div>
                </button>
                
                <button
                  data-testid="single-template-type-btn"
                  onClick={() => setTemplateType("single")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "single" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📄</div>
                    <div className="font-semibold">Single Photo</div>
                    <div className="text-xs text-gray-500 mt-1">4"×6" rectangle • 1 photo • Auto-print</div>
                  </div>
                </button>
                
                <button
                  data-testid="event-template-type-btn"
                  onClick={() => setTemplateType("event")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "event" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">🎉</div>
                    <div className="font-semibold">Event Gallery</div>
                    <div className="text-xs text-gray-500 mt-1">4"×6" • Multiple photos • QR scan</div>
                  </div>
                </button>
                
                <button
                  data-testid="branded-template-type-btn"
                  onClick={() => setTemplateType("branded")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "branded" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">🏷️</div>
                    <div className="font-semibold">Branded Card</div>
                    <div className="text-xs text-gray-500 mt-1">2.25"×3.25" • 5/6 photo + 1/6 logo</div>
                  </div>
                </button>
                
                <button
                  data-testid="postcard-template-type-btn"
                  onClick={() => setTemplateType("postcard")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "postcard" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📮</div>
                    <div className="font-semibold">Photo Postcard</div>
                    <div className="text-xs text-gray-500 mt-1">3.5"×6.5" • Photo + addresses</div>
                  </div>
                </button>
                
                <button
                  data-testid="text-postcard-template-type-btn"
                  onClick={() => setTemplateType("text-postcard")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "text-postcard" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📝</div>
                    <div className="font-semibold">Text Postcard</div>
                    <div className="text-xs text-gray-500 mt-1">3.5"×6.5" • Text only, no photo</div>
                  </div>
                </button>
              </div>
              
              {(templateType === "single" || templateType === "postcard") && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="auto-print"
                    checked={autoPrint}
                    onChange={(e) => setAutoPrint(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="auto-print-checkbox"
                  />
                  <label htmlFor="auto-print" className="text-sm font-medium text-gray-700">
                    Auto-print immediately when photo uploaded
                  </label>
                </div>
              )}
              
              {templateType === "event" && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <div className="text-sm font-medium text-yellow-800">
                    🎉 Event Gallery Mode - Perfect for live events!
                  </div>
                  <div className="text-xs text-yellow-600 mt-1">
                    Share the QR code with guests. All photos will be collected and can be downloaded individually.
                  </div>
                </div>
              )}
              
              {templateType === "branded" && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="text-sm font-medium text-purple-800">
                    🏷️ Branded Card - 2.25" × 3.25"
                  </div>
                  <div className="text-xs text-purple-600 mt-1">
                    Top 5/6: Guest photo | Bottom 1/6: Your logo/branding. Upload prefix image after creating template.
                  </div>
                </div>
              )}
              
              {(templateType === "postcard" || templateType === "text-postcard") && (
                <div className="space-y-3 p-3 bg-green-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">Postcard Fields</div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label htmlFor="from-address" className="text-xs text-gray-600">From Address</Label>
                      <textarea
                        id="from-address"
                        placeholder="From:\nPOTRAIT CHOCOLATE\n55-230 Hoea Rd\nHawi, HI 96719"
                        value={fromAddress}
                        onChange={(e) => setFromAddress(e.target.value)}
                        className="w-full text-xs p-2 border rounded-md"
                        rows="4"
                        data-testid="from-address-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="to-address" className="text-xs text-gray-600">To Address</Label>
                      <textarea
                        id="to-address"
                        placeholder="To:\nName\nAddress\nCity, State ZIP"
                        value={toAddress}
                        onChange={(e) => setToAddress(e.target.value)}
                        className="w-full text-xs p-2 border rounded-md"
                        rows="4"
                        data-testid="to-address-input"
                      />
                    </div>
                  </div>
                  
                  {templateType === "text-postcard" && (
                    <div className="space-y-2">
                      <div>
                        <Label htmlFor="ingredients" className="text-xs text-gray-600">Ingredients</Label>
                        <textarea
                          id="ingredients"
                          value={ingredients}
                          onChange={(e) => setIngredients(e.target.value)}
                          className="w-full text-xs p-2 border rounded-md"
                          rows="2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nutrition" className="text-xs text-gray-600">Nutrition Facts</Label>
                        <textarea
                          id="nutrition"
                          value={nutritionFacts}
                          onChange={(e) => setNutritionFacts(e.target.value)}
                          className="w-full text-xs p-2 border rounded-md"
                          rows="4"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className="border-0 shadow-xl backdrop-blur-sm bg-white/90 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              data-testid={`template-card-${template.id}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    <div className="flex items-center gap-2">
                      {template.template_type === "rectangle" ? "▭" : template.template_type === "single" ? "📄" : template.template_type === "event" ? "🎉" : template.template_type === "branded" ? "🏷️" : template.template_type === "postcard" ? "📮" : template.template_type === "text-postcard" ? "📝" : "⭕"}
                      {template.name}
                    </div>
                  </CardTitle>
                  <button
                    onClick={() => deleteTemplate(template.id, template.name)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                    data-testid={`delete-template-btn-${template.id}`}
                    title="Delete template"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <CardDescription>
                  {template.template_type === "event"
                    ? `Event Gallery • ${template.filled_count} photos collected`
                    : template.template_type === "branded"
                    ? `Branded Card 2.25"×3.25" • ${template.filled_count} photos`
                    : template.template_type === "single" 
                    ? `Single 4"×6" photo • ${template.filled_count} photos`
                    : template.template_type === "postcard"
                    ? `Postcard 6.5"×3.5" • ${template.filled_count}/1 filled`
                    : `${template.filled_count} / ${template.total_slots} ${template.template_type === "rectangle" ? "rectangles" : "circles"} filled`
                  }
                  {template.auto_print && " • Auto-print ON"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm font-bold text-purple-600">
                      {template.template_type === "event" || template.template_type === "branded" || template.template_type === "single"
                        ? `${template.filled_count} photos`
                        : `${Math.round((template.filled_count / template.total_slots) * 100)}%`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((template.filled_count / template.total_slots) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Access Control for sharing */}
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setSelectedTemplateForAccess(template);
                    setShowAccessControl(true);
                  }}
                  data-testid={`access-control-btn-${template.id}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Share Access ({template.allowed_emails?.length || 0} users)
                </Button>

                {/* Prefix image upload for branded templates */}
                {template.template_type === "branded" && (
                  <Button 
                    variant="outline" 
                    className="w-full bg-purple-50"
                    data-testid={`upload-prefix-btn-${template.id}`}
                    onClick={() => {
                      setSelectedTemplateForPrefix(template);
                      setShowPrefixEditor(true);
                    }}
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    {template.prefix_image_url ? "Update" : "Upload"} Prefix/Logo Image
                  </Button>
                )}

                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      data-testid={`upload-bg-btn-${template.id}`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Background
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="background-upload-dialog">
                    <DialogHeader>
                      <DialogTitle>Upload Background Image</DialogTitle>
                      <DialogDescription>
                        This background will be applied to all photos in this template
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="background-upload">Select Image</Label>
                        <Input
                          id="background-upload"
                          type="file"
                          accept="image/*"
                          data-testid="background-file-input"
                          onChange={(e) => setBackgroundFile(e.target.files[0])}
                          className="mt-2"
                        />
                      </div>
                      <Button
                        data-testid="confirm-upload-bg-btn"
                        onClick={() => uploadBackground(template.id)}
                        className="w-full"
                      >
                        Upload
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  data-testid={`view-qr-btn-${template.id}`}
                  onClick={() => viewQRCode(template.id)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  View QR Code
                </Button>

                <Button
                  data-testid={`copy-share-link-btn-${template.id}`}
                  onClick={() => {
                    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
                    const shareUrl = `${frontendUrl}/shared/${template.id}`;
                    navigator.clipboard.writeText(shareUrl);
                    alert(`Share link copied!\n\n${shareUrl}`);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Copy Share Link
                </Button>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid={`add-image-btn-${template.id}`}
                    >
                      <ImagePlus className="w-4 h-4 mr-2" />
                      Add Your Image
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="owner-image-upload-dialog">
                    <DialogHeader>
                      <DialogTitle>Upload Image</DialogTitle>
                      <DialogDescription>
                        Add an image directly to this template
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="owner-image-upload">Select Image</Label>
                        <Input
                          id="owner-image-upload"
                          type="file"
                          accept="image/*"
                          data-testid="owner-image-file-input"
                          onChange={(e) => setOwnerImageFile(e.target.files[0])}
                          className="mt-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Keep under 5MB for best results
                        </p>
                      </div>
                      <Button
                        data-testid="confirm-upload-owner-image-btn"
                        onClick={() => uploadOwnerImage(template.id)}
                        className="w-full"
                        disabled={uploadingOwnerImage || !ownerImageFile}
                      >
                        {uploadingOwnerImage ? "Processing..." : "Upload"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  data-testid={`edit-template-btn-${template.id}`}
                  onClick={() => navigate(`/admin/photo-booth/${template.id}`)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Open Editor
                </Button>

                <Button
                  data-testid={`view-images-btn-${template.id}`}
                  onClick={() => viewImages(template)}
                  variant="outline"
                  className="w-full"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  View Images ({template.filled_count})
                </Button>

                <Button
                  data-testid={`print-btn-${template.id}`}
                  onClick={() => printTemplate(template.id, template.name)}
                  variant="outline"
                  className="w-full"
                  disabled={template.template_type !== 'text-postcard' && template.filled_count === 0}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Now
                </Button>

                <Button
                  data-testid={`download-pdf-btn-${template.id}`}
                  onClick={() => downloadPDF(template.id, template.name)}
                  variant="outline"
                  className="w-full"
                  disabled={template.template_type !== 'text-postcard' && template.filled_count === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>

                <Button
                  data-testid={`print-monitor-btn-${template.id}`}
                  onClick={() => window.open(`/print-monitor/${template.id}`, '_blank')}
                  variant="outline"
                  className="w-full bg-green-50 hover:bg-green-100 border-green-300"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Print Monitor
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={showQR} onOpenChange={setShowQR}>
          <DialogContent data-testid="qr-code-dialog">
            <DialogHeader>
              <DialogTitle>QR Code for Upload</DialogTitle>
              <DialogDescription>
                Share this QR code with guests to upload their photos
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center p-6">
              {qrCodeUrl && (
                <img 
                  src={qrCodeUrl} 
                  alt="QR Code" 
                  data-testid="qr-code-image"
                  className="w-64 h-64 border-4 border-gray-200 rounded-lg shadow-lg" 
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showPrinterSetup} onOpenChange={setShowPrinterSetup}>
          <DialogContent data-testid="printer-setup-dialog">
            <DialogHeader>
              <DialogTitle>Configure Printer</DialogTitle>
              <DialogDescription>
                {availablePrinters.length === 0 ? (
                  <div className="space-y-4 mt-4">
                    <p className="text-yellow-600 font-medium">⚠️ No printers detected</p>
                    <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
                      <p className="font-medium">To enable printing:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Install CUPS: <code className="bg-gray-200 px-2 py-1 rounded">sudo apt-get install cups</code></li>
                        <li>Connect your printer (USB or network)</li>
                        <li>Add printer: <code className="bg-gray-200 px-2 py-1 rounded">sudo lpadmin -p MyPrinter -E</code></li>
                        <li>Refresh this page</li>
                      </ol>
                    </div>
                    <div className="pt-2">
                      <p className="text-sm text-gray-600">For now, you can download PDFs and print manually:</p>
                      <Button
                        onClick={() => {
                          setShowPrinterSetup(false);
                          if (selectedTemplateToPrint) {
                            downloadPDF(selectedTemplateToPrint.id, selectedTemplateToPrint.name);
                          }
                        }}
                        className="w-full mt-2"
                      >
                        Download PDF Instead
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 mt-4">
                    <p>Select a printer for this template:</p>
                    <div className="space-y-2">
                      {availablePrinters.map((printer) => (
                        <Button
                          key={printer}
                          onClick={() => configurePrinter(printer)}
                          variant="outline"
                          className="w-full"
                          data-testid={`select-printer-${printer}`}
                        >
                          <Printer className="w-4 h-4 mr-2" />
                          {printer}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        <Dialog open={showImageView} onOpenChange={setShowImageView}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="images-view-dialog">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name} - Images ({templateImages.length})</DialogTitle>
              <DialogDescription>
                {selectedTemplate?.template_type === "event" 
                  ? "Click download button on each image to save individually"
                  : "Drag images to rearrange their positions"
                }
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-4 p-4">
              {templateImages.map((img) => (
                <div
                  key={img.id}
                  className="relative group"
                  data-testid={`image-item-${img.id}`}
                >
                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-purple-500 transition-all">
                    <img
                      src={`${API}/images/${img.id}`}
                      alt={`Photo ${img.circle_index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    #{img.circle_index + 1}
                  </div>
                  {img.user_name && (
                    <div className="absolute bottom-12 left-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs truncate">
                      {img.user_name}
                      {img.user_contact && <span className="ml-1 opacity-75">• {img.user_contact}</span>}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a
                      href={`${API}/images/${img.id}/download`}
                      download
                      data-testid={`download-image-btn-${img.id}`}
                      className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-full shadow-lg transition-colors"
                      title="Download image"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => deleteImage(img.id)}
                      data-testid={`delete-image-btn-${img.id}`}
                      className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg transition-colors"
                      title="Delete image"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {selectedTemplate?.template_type !== "event" && (
                    <div className="mt-2">
                      <Input
                        type="number"
                        min="0"
                        max={selectedTemplate?.total_slots - 1}
                        value={img.circle_index}
                        data-testid={`position-input-${img.id}`}
                        onChange={(e) => {
                          const newIndex = parseInt(e.target.value);
                          if (!isNaN(newIndex)) {
                            moveImage(img.id, newIndex);
                          }
                        }}
                        className="text-xs text-center"
                        placeholder="Position"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Access Control Dialog */}
        <Dialog open={showAccessControl} onOpenChange={setShowAccessControl}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Share Template Access</DialogTitle>
              <DialogDescription>
                {selectedTemplateForAccess?.name} - Add email addresses to grant access
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={accessEmail}
                  onChange={(e) => setAccessEmail(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={addEmailAccess} className="bg-purple-500 hover:bg-purple-600">
                  Add
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Users with access:</Label>
                {selectedTemplateForAccess?.allowed_emails?.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedTemplateForAccess.allowed_emails.map((email, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">{email}</span>
                        <button
                          onClick={() => removeEmailAccess(email)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No users have access yet</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Prefix Image Editor */}
        <PrefixImageEditor
          isOpen={showPrefixEditor}
          onClose={() => {
            setShowPrefixEditor(false);
            setSelectedTemplateForPrefix(null);
          }}
          onSave={async (formData) => {
            if (selectedTemplateForPrefix) {
              await axios.post(`${API}/templates/${selectedTemplateForPrefix.id}/prefix-image`, formData);
              fetchTemplates();
            }
          }}
          templateId={selectedTemplateForPrefix?.id}
        />
      </div>
    </div>
  );
}